"use client";

/**
 * Per-row actions dropdown for the Enrollments tab (Phase J · Task 57).
 *
 * Status-aware menu: only the actions valid for the current row's
 * status are shown. The menu uses our `DropdownMenu` primitive from
 * `@/components/ui/dropdown-menu` (Radix) with a kebab trigger
 * (`IconDotGrid1x3Horizontal`, the same affordance the Tasks side
 * panel uses).
 *
 * Visibility per spec §10.4:
 *   - active     → Pause, Cancel
 *   - paused     → Resume, Cancel
 *   - scheduled  → Pause, Cancel
 *   - cancelled  → Restart
 *   - completed  → Restart
 *   - bounced / error → no actions (terminal, no restart)
 *
 * Click-through guard: each menu item calls `stopPropagation`
 * on the row click so the per-enrollment detail drawer (Task 58)
 * doesn't open at the same time as the action.
 */

import { useState, type ReactNode } from "react";
import { useQueryClient } from "@tanstack/react-query";

import type { ActionMenuKit } from "@/components/ui/action-menu-kit";
import {
  ContextMenuItem,
  ContextMenuSeparator,
  ContextMenuSub,
  ContextMenuSubContent,
  ContextMenuSubTrigger,
} from "@/components/ui/context-menu";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Icon } from "@/components/ui/icon";
import { showToast } from "@/components/ui/sonner";
import { getApiErrorMessage } from "@/lib/api/error";
import {
  getListEnrollmentsQueryKey,
  useClassifyEnrollment,
} from "@/lib/api/generated";

import type { EnrollmentListItem, EnrollmentStatus } from "../../lib/types";

import { CancelEnrollmentModal } from "./cancel-modal";
import { PauseEnrollmentModal } from "./pause-modal";
import { ResumeEnrollmentModal } from "./resume-modal";

type OutcomeLabel = "interested" | "not_interested" | null;

type ModalKind = "pause" | "resume" | "cancel" | null;

interface ActionsMenuProps {
  enrollment: EnrollmentListItem;
  jobId: string;
  sequenceId: string;
  /**
   * Override the kebab trigger styling. Defaults to the row-hover reveal used
   * by the list table; the Enrollments table's always-visible trailing action
   * group passes a non-fading variant.
   */
  triggerClassName?: string;
  /** Shared action state when the row also exposes a context menu. */
  actions?: EnrollmentRowActionsState;
}

const DEFAULT_TRIGGER_CLS =
  "flex size-7 cursor-pointer items-center justify-center rounded-md text-contrast/70 opacity-0 transition-colors group-hover/row:opacity-100 hover:bg-white/6 hover:text-contrast focus-visible:opacity-100 data-[state=open]:opacity-100";

interface ActionVisibility {
  pause: boolean;
  resume: boolean;
  cancel: boolean;
}

function computeVisibility(status: EnrollmentStatus): ActionVisibility {
  switch (status) {
    case "active":
    case "scheduled":
      return { pause: true, resume: false, cancel: true };
    case "paused":
      return { pause: false, resume: true, cancel: true };
    default:
      return { pause: false, resume: false, cancel: false };
  }
}

export interface EnrollmentRowActionsState {
  enrollment: EnrollmentListItem;
  visibility: ActionVisibility;
  canChangeOutcome: boolean;
  currentOutcome: OutcomeLabel | string | null;
  classifyPending: boolean;
  anyVisible: boolean;
  setOpenModal: (kind: ModalKind) => void;
  handleOutcome: (label: OutcomeLabel) => void;
  modals: ReactNode;
}

export function useEnrollmentRowActions({
  enrollment,
  jobId,
  sequenceId,
}: {
  enrollment: EnrollmentListItem;
  jobId: string;
  sequenceId: string;
}): EnrollmentRowActionsState {
  const [openModal, setOpenModal] = useState<ModalKind>(null);
  const visibility = computeVisibility(enrollment.status);
  const queryClient = useQueryClient();
  const classify = useClassifyEnrollment();

  const canChangeOutcome =
    Boolean(enrollment.reply_classified) ||
    Boolean(enrollment.reply_classification) ||
    (enrollment.replies_received_count ?? 0) > 0;
  const currentOutcome = (enrollment.reply_classification ?? null) as
    | OutcomeLabel
    | string
    | null;

  const anyVisible =
    visibility.pause ||
    visibility.resume ||
    visibility.cancel ||
    canChangeOutcome;

  const handleOutcome = (label: OutcomeLabel) => {
    void (async () => {
      try {
        await classify.mutateAsync({
          jobId,
          sequenceId,
          enrollmentId: enrollment.id,
          data: { label },
        });
        await queryClient.invalidateQueries({
          queryKey: getListEnrollmentsQueryKey(jobId, sequenceId),
          exact: false,
        });
        showToast.success("Outcome updated.");
      } catch (err) {
        showToast.error("Couldn't update the outcome. Please try again.", {
          description: getApiErrorMessage(err),
        });
      }
    })();
  };

  const modals = (
    <>
      <PauseEnrollmentModal
        enrollment={enrollment}
        jobId={jobId}
        sequenceId={sequenceId}
        open={openModal === "pause"}
        onOpenChange={(next) => setOpenModal(next ? "pause" : null)}
      />
      <ResumeEnrollmentModal
        enrollment={enrollment}
        jobId={jobId}
        sequenceId={sequenceId}
        open={openModal === "resume"}
        onOpenChange={(next) => setOpenModal(next ? "resume" : null)}
      />
      <CancelEnrollmentModal
        enrollment={enrollment}
        jobId={jobId}
        sequenceId={sequenceId}
        open={openModal === "cancel"}
        onOpenChange={(next) => setOpenModal(next ? "cancel" : null)}
      />
    </>
  );

  return {
    enrollment,
    visibility,
    canChangeOutcome,
    currentOutcome,
    classifyPending: classify.isPending,
    anyVisible,
    setOpenModal,
    handleOutcome,
    modals,
  };
}

function renderEnrollmentActionsMenuItems(
  state: EnrollmentRowActionsState,
  kit: ActionMenuKit,
) {
  const {
    enrollment,
    visibility,
    canChangeOutcome,
    currentOutcome,
    classifyPending,
    setOpenModal,
    handleOutcome,
  } = state;
  const { Item, Separator, Sub, SubTrigger, SubContent } = kit;

  if (!Sub || !SubTrigger || !SubContent) {
    throw new Error("Enrollment actions require submenu kit components.");
  }

  return (
    <>
      {visibility.pause && (
        <Item
          onSelect={() => setOpenModal("pause")}
          data-testid={`enrollment-action-pause-${enrollment.id}`}
        >
          <Icon name="IconPause" size={14} />
          Pause
        </Item>
      )}
      {visibility.resume && (
        <Item
          onSelect={() => setOpenModal("resume")}
          data-testid={`enrollment-action-resume-${enrollment.id}`}
        >
          <Icon name="IconPlay" size={14} />
          Resume
        </Item>
      )}
      {visibility.cancel && (visibility.pause || visibility.resume) && (
        <Separator />
      )}
      {visibility.cancel && (
        <Item
          variant="destructive"
          onSelect={() => setOpenModal("cancel")}
          data-testid={`enrollment-action-cancel-${enrollment.id}`}
        >
          <Icon name="IconCircleX" size={14} />
          Cancel
        </Item>
      )}
      {canChangeOutcome && (
        <>
          {(visibility.pause || visibility.resume || visibility.cancel) && (
            <Separator />
          )}
          <Sub>
            <SubTrigger
              data-testid={`enrollment-action-outcome-${enrollment.id}`}
              disabled={classifyPending}
            >
              <Icon name="IconTag" size={14} />
              Change outcome
            </SubTrigger>
            <SubContent>
              <Item
                disabled={classifyPending}
                onSelect={() => handleOutcome("interested")}
                data-testid={`enrollment-outcome-interested-${enrollment.id}`}
              >
                <Icon
                  name="IconCheckCircle2"
                  size={14}
                  className="text-[#15803d] dark:text-[#66dc7e]"
                />
                <span
                  className={
                    currentOutcome === "interested"
                      ? "font-medium text-heading"
                      : undefined
                  }
                >
                  Interested
                </span>
              </Item>
              <Item
                disabled={classifyPending}
                onSelect={() => handleOutcome("not_interested")}
                data-testid={`enrollment-outcome-not-interested-${enrollment.id}`}
              >
                <Icon
                  name="IconExclamationCircle"
                  size={14}
                  className="text-[#b91c1c] dark:text-[#e87c7d]"
                />
                <span
                  className={
                    currentOutcome === "not_interested"
                      ? "font-medium text-heading"
                      : undefined
                  }
                >
                  Not interested
                </span>
              </Item>
              <Item
                disabled={classifyPending}
                onSelect={() => handleOutcome(null)}
                data-testid={`enrollment-outcome-clear-${enrollment.id}`}
              >
                <Icon
                  name="IconCircleX"
                  size={14}
                  className="text-muted-foreground"
                />
                Clear classification
              </Item>
            </SubContent>
          </Sub>
        </>
      )}
    </>
  );
}

const enrollmentActionsDropdownKit: ActionMenuKit = {
  Item: DropdownMenuItem,
  Separator: DropdownMenuSeparator,
  Sub: DropdownMenuSub,
  SubTrigger: DropdownMenuSubTrigger,
  SubContent: DropdownMenuSubContent,
};

const enrollmentActionsContextKit: ActionMenuKit = {
  Item: ContextMenuItem,
  Separator: ContextMenuSeparator,
  Sub: ContextMenuSub,
  SubTrigger: ContextMenuSubTrigger,
  SubContent: ContextMenuSubContent,
};

export function EnrollmentActionsContextMenuContent({
  actions,
}: {
  actions: EnrollmentRowActionsState;
}) {
  if (!actions.anyVisible) return null;
  return renderEnrollmentActionsMenuItems(
    actions,
    enrollmentActionsContextKit,
  );
}

export function ActionsMenu({
  enrollment,
  jobId,
  sequenceId,
  triggerClassName,
  actions: actionsProp,
}: ActionsMenuProps) {
  const internalActions = useEnrollmentRowActions({
    enrollment,
    jobId,
    sequenceId,
  });
  const actions = actionsProp ?? internalActions;

  if (!actions.anyVisible) return null;

  const stop = (event: React.MouseEvent) => event.stopPropagation();

  return (
    <>
      <div onClick={stop} className="flex justify-end">
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button
              type="button"
              aria-label="Enrollment actions"
              data-testid={`enrollment-row-actions-${enrollment.id}`}
              className={triggerClassName ?? DEFAULT_TRIGGER_CLS}
            >
              <Icon name="IconDotGrid1x3Horizontal" size={15} />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" sideOffset={4}>
            {renderEnrollmentActionsMenuItems(
              actions,
              enrollmentActionsDropdownKit,
            )}
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
      {actionsProp ? null : actions.modals}
    </>
  );
}

export function EnrollmentRowActions({
  enrollment,
  jobId,
  sequenceId,
  children,
}: {
  enrollment: EnrollmentListItem;
  jobId: string;
  sequenceId: string;
  children: (actions: EnrollmentRowActionsState) => ReactNode;
}) {
  const actions = useEnrollmentRowActions({ enrollment, jobId, sequenceId });
  return (
    <>
      {children(actions)}
      {actions.modals}
    </>
  );
}
